import { z } from 'zod';
import {
  ColorSetIdSchema,
  PACKAGE_TIER_KEY_VALUES,
  PLATFORM_VERTICAL_KEY_VALUES,
  ThemeIdSchema,
  type ColorSetId,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { ProjectLocalePolicySchema } from '../locales/platformLocales';
import {
  CORNER_ENUM_KEY_VALUES,
  SPACING_ENUM_KEYS,
  SPACING_ENUM_KEY_VALUES,
  TYPOGRAPHY_ENUM_KEY_VALUES,
} from '../theme/types';
import {
  isValidThemeColorSet,
  resolveDefaultColorSetId,
  resolveThemeCornerEnum,
  resolveThemeTypographyEnum,
} from '../theme/themeResolve';
import { getVerticalDefinition } from './platformVerticalMatrix';

/** Confirms vertical, tier, theme, and color-set form a valid matrix row before project bootstrap. */
export function isValidOnboardingSelection(
  vertical: string,
  tier: string,
  themeId: string,
  colorsetId: string,
): boolean {
  const definition = getVerticalDefinition(vertical);
  if (!definition) {
    return false;
  }
  if (!(PACKAGE_TIER_KEY_VALUES as readonly string[]).includes(tier)) {
    return false;
  }
  if (!definition.themes.some((theme: { id: ThemeId; name: string }) => theme.id === themeId)) {
    return false;
  }
  return isValidThemeColorSet(themeId, colorsetId);
}

export function resolveOnboardingColorSetId(themeId: ThemeId, colorsetId: ColorSetId): ColorSetId;
export function resolveOnboardingColorSetId(themeId: string, colorsetId: string): ColorSetId | string;
export function resolveOnboardingColorSetId(themeId: string, colorsetId: string): ColorSetId | string {
  if (isValidThemeColorSet(themeId, colorsetId)) {
    return colorsetId as ColorSetId;
  }
  return resolveDefaultColorSetId(themeId) ?? colorsetId;
}

export const StudioPassSchema = z.object({
  email: z.string().email({ message: 'A valid studio pass email is required.' }),
  displayName: z.string().min(1).max(80).optional(),
});

export const ProjectCreationPayloadSchema = z
  .object({
    vertical: z.enum(PLATFORM_VERTICAL_KEY_VALUES),
    packageTier: z.enum(PACKAGE_TIER_KEY_VALUES),
    themeId: ThemeIdSchema,
    colorsetId: ColorSetIdSchema,
    spacingEnum: z.enum(SPACING_ENUM_KEY_VALUES).default(SPACING_ENUM_KEYS.balanced),
    cornerEnum: z.enum(CORNER_ENUM_KEY_VALUES).optional(),
    typographyEnum: z.enum(TYPOGRAPHY_ENUM_KEY_VALUES).optional(),
    studioPass: StudioPassSchema,
    /** When true, always bootstrap a new draft (owner gate / reconfigure), never reuse pass token draft. */
    freshConfig: z.boolean().optional(),
    userId: z.string().min(1).optional(),
  })
  .merge(ProjectLocalePolicySchema)
  .superRefine((payload, ctx) => {
    if (
      !isValidOnboardingSelection(
        payload.vertical,
        payload.packageTier,
        payload.themeId,
        payload.colorsetId,
      )
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Theme, color-set, and package tier must match the selected vertical in PLATFORM_VERTICAL_MATRIX.',
        path: ['colorsetId'],
      });
    }
  })
  .transform((payload) => ({
    ...payload,
    cornerEnum: payload.cornerEnum ?? resolveThemeCornerEnum(payload.themeId),
    typographyEnum: payload.typographyEnum ?? resolveThemeTypographyEnum(payload.themeId),
  }));

export type ProjectCreationPayload = z.infer<typeof ProjectCreationPayloadSchema>;
export type StudioPassPayload = z.infer<typeof StudioPassSchema>;
